/** @format */

import {useCallback, useRef, useState} from 'react';

import {useNavigate} from 'react-router-dom';

import {Alert, Box, LinearProgress} from '@mui/material';
import {add, format} from 'date-fns';
import {ru} from 'date-fns/locale';
import {Trans} from 'react-i18next';

import BalanceIcon from '@mui/icons-material/Balance';
import FilterListIcon from '@mui/icons-material/FilterList';

import {useFlags} from 'launchdarkly-react-client-sdk';

import {AccountsCompanyMediaplanType} from 'graphql/generated/types';
import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {HoverDialog} from 'components/HoverDialog';

import TableIcon from './images/table-icon.svg?react';

import {useGetCampaignParamsQuery} from '../../graphql/__generated__';
import {useCampaignCardsData} from './hooks/useCampaignCardsData';
import {getCurrentMediaplan} from './helpers/getCurrentMediaplan';
import {getCurrentMediaplanPeriod} from './helpers/getCurrentMediaplanPeriod';
import {getCurrentPeriod} from './helpers/getCurrentPeriod';
import {getPrimaryKpi} from './helpers/getPrimaryKpi';
import {
  HeadBox,
  HeadTextBox,
  HeadingText,
  Root,
  SubHeadingText,
  ViewModeButton,
  ViewModeButtonsBox,
  CampaignsCount,
  MediaplanBannerWrapper,
  MediaplanBannerText,
  MediaplanBannerButton,
  FilterButtonsContainer,
  FilterIconWrapper,
} from './styles';
import {LastCheckType, SensorGroupsType} from './types';
import {MEDIAPLAN_LOOKBACK_YEARS} from '../../constants';
import {CampaignsTable} from './components/CampaignsTable';
import {CampaignCards} from './components/CampaignCards';
import {MediaplanTooltipContent} from './components/MediaplanTooltipContent';

interface Props {
  lastChecks: ReadonlyArray<LastCheckType>;
  sensorGroups: SensorGroupsType;
  mediaplanType?: AccountsCompanyMediaplanType | null;
}

const Campaigns = ({lastChecks, sensorGroups, mediaplanType}: Props) => {
  const {isCampaignsCardViewEnable} = useFlags();
  const now = new Date();
  const navigate = useNavigate();
  const cardsBtnRef = useRef<HTMLButtonElement>(null);
  const {
    currentCompany: {id: companyId, uuid: companyUuid},
  } = useCompanyContext();
  const {mixpanel} = useMixpanelContext();

  const {
    data: mediaplansData,
    loading: mediaplansLoading,
    error: mediaplansError,
  } = useGetCampaignParamsQuery({
    variables: {
      startDate: format(add(now, {years: MEDIAPLAN_LOOKBACK_YEARS}), 'yyyy-MM-dd'),
      endDate: format(now, 'yyyy-MM-dd'),
    },
  });

  const mediaplans = mediaplansData?.currentUser?.currentCompany?.mediaplans;
  const hasMediaplans = mediaplans ? mediaplans.length > 0 : false;

  const [viewMode, setViewMode] = useState<'cards' | 'table'>(hasMediaplans ? 'cards' : 'table');
  const currentMediaplan = mediaplans && getCurrentMediaplan(mediaplans, now);

  const period = currentMediaplan
    ? getCurrentMediaplanPeriod({
        startOn: currentMediaplan.startOn,
        endOn: currentMediaplan.endOn,
        today: now,
      })
    : getCurrentPeriod(now);

  const primaryKpi = currentMediaplan ? getPrimaryKpi(currentMediaplan) : undefined;

  const {visibleCampaignsCount, totalCampaignsCount} = useCampaignCardsData({
    period,
    lastChecks,
    sensorGroups,
    primaryKpi,
  });

  const handleCardsViewClick = () => {
    if (hasMediaplans) {
      mixpanel.track('click_top_weighted_campaigns_view', {project_uuid: companyUuid});
      setViewMode('cards');
    }
  };

  const handleTableViewClick = () => {
    mixpanel.track('click_campaigns_table_view', {project_uuid: companyUuid});
    setViewMode('table');
  };

  const handleMediaplanSettingsClick = () => {
    mixpanel.track('click_top_wighted_mediaplan_settings', {project_uuid: companyUuid});
    navigate(`/home/project/periods?companyId=${companyId}&edit=1`);
  };

  const handleMoreSpendClick = () => {
    mixpanel.track('click_wheight_campaign_more_spend', {project_uuid: companyUuid});
  };

  const handleCampaignRowClick = useCallback(() => {
    mixpanel.track('click_project_campaign_table_row', {project_uuid: companyUuid});
  }, [mixpanel, companyUuid]);

  if (mediaplansError) {
    return <Alert severity='error'>{mediaplansError.message}</Alert>;
  }

  if (mediaplansLoading || !mediaplans) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const fromDate = format(period.startDate, 'd MMMM', {locale: ru});
  const toDate = format(period.endDate, 'd MMMM', {locale: ru});

  return (
    <Root>
      <HeadBox>
        <HeadTextBox>
          <HeadingText>
            <Trans>Кампании</Trans>
          </HeadingText>
          <SubHeadingText>
            {fromDate} – {toDate}
          </SubHeadingText>
        </HeadTextBox>

        {isCampaignsCardViewEnable && (
          <>
            {viewMode === 'table' && !hasMediaplans && (
              <HoverDialog
                anchorRef={cardsBtnRef}
                content={<MediaplanTooltipContent />}
                placement='bottom-start'
                leaveDelay={150}
                offset={[0, 4]}
              />
            )}

            <ViewModeButtonsBox>
              <Box ref={cardsBtnRef}>
                <ViewModeButton
                  active={viewMode === 'cards'}
                  startIcon={<BalanceIcon />}
                  onClick={handleCardsViewClick}
                  disabled={!hasMediaplans}
                >
                  Топ взвешенных
                </ViewModeButton>
              </Box>
              <ViewModeButton
                active={viewMode === 'table'}
                onClick={handleTableViewClick}
                startIcon={<TableIcon />}
              >
                Таблица кампаний
              </ViewModeButton>
            </ViewModeButtonsBox>

            {viewMode === 'cards' && (
              <FilterButtonsContainer>
                <FilterIconWrapper>
                  <FilterListIcon />
                </FilterIconWrapper>
                <ViewModeButton active onClick={handleMoreSpendClick}>
                  Больше тратят
                </ViewModeButton>
                <ViewModeButton disabled>Больше приносят</ViewModeButton>
              </FilterButtonsContainer>
            )}

            {!hasMediaplans && (
              <MediaplanBannerWrapper>
                <MediaplanBannerText>
                  Настройте медиаплан, чтобы видеть
                  <br /> взвешенные кампании и данные по KPI
                </MediaplanBannerText>
                <MediaplanBannerButton onClick={handleMediaplanSettingsClick}>
                  Настроить
                </MediaplanBannerButton>
              </MediaplanBannerWrapper>
            )}

            {viewMode === 'cards' && (
              <CampaignsCount>
                Показано {visibleCampaignsCount} из {totalCampaignsCount}
              </CampaignsCount>
            )}
          </>
        )}
      </HeadBox>

      {viewMode === 'table' ? (
        <CampaignsTable
          lastChecks={lastChecks}
          sensorGroups={sensorGroups}
          mediaplanType={mediaplanType}
          primaryKpi={primaryKpi}
          period={period}
          onRowClick={handleCampaignRowClick}
        />
      ) : (
        <CampaignCards
          lastChecks={lastChecks}
          sensorGroups={sensorGroups}
          mediaplanType={mediaplanType}
          primaryKpi={primaryKpi}
          period={period}
        />
      )}
    </Root>
  );
};

export {Campaigns};
